import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import type {
  ActiveControls,
  EndedControls,
  LiveSessionDetail,
  LiveSessionRow,
  LiveSessionsMetrics,
  PaginatedResponse,
  ProgramOption,
  RecentlyEndedSession,
} from './types';

const REFETCH_INTERVAL_MS = 5000;
const DEFAULT_PER_PAGE = 25;

interface UseLiveSessionsParams {
  controls: ActiveControls;
  perPage?: number;
}

const buildActiveParams = (controls: ActiveControls, perPage: number) => ({
  dir: controls.sort.dir,
  heartbeat: controls.filters.heartbeat,
  page: controls.page,
  per_page: perPage,
  program_ids: controls.filters.programIds,
  q: controls.q || undefined,
  sort: controls.sort.key,
});

export const useLiveSessions = ({
  controls, perPage = DEFAULT_PER_PAGE,
}: UseLiveSessionsParams) => useQuery({
  keepPreviousData: true,
  queryFn: () => axios
    .get<PaginatedResponse<LiveSessionRow>>(
    Routes.plato_api_sysadmin_live_sessions_path(buildActiveParams(controls, perPage)),
  )
    .then(res => res.data),
  queryKey: ['LiveSessionsMonitor', 'sessions', buildActiveParams(controls, perPage)],
  refetchInterval: REFETCH_INTERVAL_MS,
  refetchIntervalInBackground: false,
});

export const useLiveSessionsMetrics = () => useQuery({
  queryFn: () => axios
    .get<{ data: LiveSessionsMetrics }>(Routes.plato_api_metrics_sysadmin_live_sessions_path())
    .then(res => res.data.data),
  queryKey: ['LiveSessionsMonitor', 'metrics'],
  refetchInterval: REFETCH_INTERVAL_MS,
  refetchIntervalInBackground: false,
});

export const useLiveSessionDetail = (sessionId: string | null) => useQuery({
  enabled: sessionId !== null,
  queryFn: () => axios
    .get<{ data: LiveSessionDetail }>(Routes.plato_api_sysadmin_live_session_path(sessionId as string))
    .then(res => res.data.data),
  queryKey: ['LiveSessionsMonitor', 'session', sessionId],
  refetchInterval: REFETCH_INTERVAL_MS,
  refetchIntervalInBackground: false,
});

interface UseRecentlyEndedParams {
  controls: EndedControls;
  enabled?: boolean;
  perPage?: number;
}

const buildEndedParams = (controls: EndedControls, perPage: number) => ({
  dir: controls.sort.dir,
  end_reason: controls.filters.endReason,
  page: controls.page,
  per_page: perPage,
  program_ids: controls.filters.programIds,
  q: controls.q || undefined,
  sort: controls.sort.key,
  within_hours: controls.filters.withinHours,
});

export const useRecentlyEndedSessions = ({
  controls, enabled = true, perPage = DEFAULT_PER_PAGE,
}: UseRecentlyEndedParams) => useQuery({
  enabled,
  keepPreviousData: true,
  queryFn: () => axios
    .get<PaginatedResponse<RecentlyEndedSession>>(
    Routes.plato_api_recently_ended_sysadmin_live_sessions_path(buildEndedParams(controls, perPage)),
  )
    .then(res => res.data),
  queryKey: ['LiveSessionsMonitor', 'recentlyEnded', buildEndedParams(controls, perPage)],
  refetchInterval: REFETCH_INTERVAL_MS,
  refetchIntervalInBackground: false,
});

export const useProgramOptions = () => useQuery({
  queryFn: () => axios
    .get<{ data: ProgramOption[] }>(Routes.plato_api_program_options_sysadmin_live_sessions_path())
    .then(res => res.data.data),
  queryKey: ['LiveSessionsMonitor', 'programOptions'],
  staleTime: 60_000,
});
